
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {catchError, map} from 'rxjs/operators';
import {Observable, throwError} from 'rxjs';
import {isPlatformBrowser} from '@angular/common';
import {ErrorService as ErrorHandler} from '../error/error.service';
import {GenericResponseInterface} from '../../networking/models/shared/responses/generic-response';
import {LocalStorageManager} from '../../manager/local-storage/local-storage.manager';
import {environment} from '../../../../environments/environment';
import {countryConfig} from '../../../../country-config/country-config';
import { BrazeUtil } from 'src/app/ui/shared/utilities/braze/braze.util';

let httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

const httpOptionsBasic = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

let countryTime = new Date();
let timetostring = countryTime.toString()
let split = timetostring.split('(')
let country = split[1].substring(0, split[1].length - 1);
let finalCountry = country.replace('Standard Time', '');
let finalCountryEs = finalCountry.replace('hora estándar de', '');
finalCountryEs = finalCountryEs.replace('hora de', '');

@Injectable({
  providedIn: 'root',
})
export class RequestService {
  apiKey = '';
  remoteConfig: any;
  isBrowser;
  ssrTokens = countryConfig.ssrTokens;

  static buildApiUrl(params?, id?): string {
    let url = '';

    if (params) {
      for (const prop in params) {
        if (params.hasOwnProperty(prop)) {
          url += `${prop}=${params[prop]}&`;
        }
      }

      url = url.substr(0, url.length - 1);
      url = `?${url}`;
    }

    if (id) {
      id = id ? (`/${id}`) : '';
      url = id + url;
    }

    return url;
  }

  static getTokens(customer) {
    if (!customer?.token) {
      return '';
    }
    return `token=${customer.token.token}&tokenIdWebSafe=${customer.token.tokenIdWebSafe}`;
  }

  constructor(private http: HttpClient,
              private errorHandler: ErrorHandler,
              private lsm: LocalStorageManager,
              private brazeUtil: BrazeUtil,
              @Inject(PLATFORM_ID) platformId) {
    this.isBrowser = isPlatformBrowser(platformId) && !(navigator.userAgent.indexOf('Chrome-Lighthouse') > -1);
    this.apiKey = !environment.deploymentVersion.developmentMode ? environment.apiKey.production : environment.apiKey.develop;
  }

  getTokens(customer) {
    if (!this.isBrowser) {
      return `token=${this.ssrTokens.token.token}&tokenIdWebSafe=${this.ssrTokens.token.tokenIdWebSafe}`;
    }
    if (!customer?.token) {
      return '';
    }
    return `token=${customer.token.token}&tokenIdWebSafe=${customer.token.tokenIdWebSafe}`;
  }

  buildReqUrl(api, withToken?, withSecureKey?) {
    let url = api;
    const token = this.lsm.has('customer') || !this.isBrowser ? this.getTokens(this.lsm.get('customer')) : '';

    url += withToken ? url.indexOf('?') !== -1 ? (`&${token}`) : `?${token}` : '';

    if (withSecureKey) {
      url += url.indexOf('?') !== -1 ? (`&key=${this.apiKey}`) : `?key=${this.apiKey}`;
      return url;
    }

    return url;
  }

  request(method, api, data, id?, params?, withToken?, withSecureKey = true, basicHeader = false, dataHeader?, addDeviceId = false) {
    const parsedParams = RequestService.buildApiUrl(params, id);
    const url = this.buildReqUrl(api + parsedParams, withToken, withSecureKey);
    let config: any = [url];
    const sourceSend = this.isBrowser ? window.screen.width <= 991 ? 'RESPONSIVE' : 'WEB' : 'WEB';
    if (data && method !== 'delete') { config.push(data); }
    if (dataHeader) {
      const httpSend = {
        'Content-Type': 'application/json',
        ...dataHeader,
      };

      if (!dataHeader.noSendCountry) {
        httpSend.country = countryConfig.indicator;
      } else {
        delete dataHeader.noSendCountry;
      }

      httpOptions = {
        headers: new HttpHeaders(httpSend),
      };
    } else {

      const headersToAdd = {
        'Content-Type': 'application/json',
        country: countryConfig.indicator,
        source: sourceSend,
        ...dataHeader,
        finalCountry: finalCountryEs,
      }
      if(headersToAdd){
        headersToAdd['device-id'] = this.getDeviceId();
      } 

      httpOptions = {
        headers: new HttpHeaders(headersToAdd),
      };
    }

    if (environment.multiCountry) {
      if (method) { config.push(basicHeader ? httpOptionsBasic : httpOptions); }
      config.push(basicHeader ? httpOptionsBasic : httpOptions);
    } else {
      config.push(httpOptionsBasic);
    }
    if (data && method === 'delete') {
      config = config?.map(item => {
        if(item?.headers) item = {...item, ...data};
        return item;
      });
    }
    return this.http[method](...config)
      .pipe(
        map((response: GenericResponseInterface) => {
          if (api.search('orderEndpoint/v2/paymentMethods') !== -1) {
            response.newData = response.data;
            delete response.data;
          }
          return response?.data ?? response;
        }),
        catchError(error => throwError(this.errorHandler.handleError(api, error))),
      );
  }

  getDeviceId() {
    const anonymous = 'ANONIMO'
    return this.brazeUtil.getDeviceId() || anonymous;
  } 

  get(api, params?, id?, withToken?, withSecureKey?, basicHeader?, dataHeader?, addDeviceId?): Observable<any> {
    return this.request('get', api, null, id, params, withToken, withSecureKey, basicHeader, dataHeader, addDeviceId);
  }

  post(api, data, params?, withToken?, withSecureKey?, basicHeader?, dataHeader?, addDeviceId?): Observable<any> {
    return this.request('post', api, data, null, params, withToken, withSecureKey, basicHeader, dataHeader, addDeviceId);
  }

  patch(api, data = {}, params?, id?, withToken?, withSecureKey?, basicHeader?, dataHeader?, addDeviceId?): Observable<any> {
    return this.request('patch', api, data, id, params, withToken, withSecureKey, basicHeader, dataHeader, addDeviceId);
  }

  put(api, data = {}, params?, id?, withToken?, withSecureKey?, basicHeader?, dataHeader?, addDeviceId?): Observable<any> {
    return this.request('put', api, data, id, params, withToken, withSecureKey, basicHeader, dataHeader, addDeviceId);
  }

  delete(api, id, params?, withToken?, withSecureKey?, basicHeader?, dataHeader?, data?, addDeviceId?): Observable<any> {
    return this.request('delete', api, data, id, params, withToken, withSecureKey, basicHeader, dataHeader, addDeviceId);
  }

}
